<script setup>
/**
 * Modal de confirmacao para apagar uma conversa.
 *
 * Aceleres 2026-08-28: extraido de ChatList.vue (253 linhas: script + template
 * + CSS). O bloco era autocontido -- nada mais no ChatList lia
 * showDeleteConversationPopup, isDeletingConversation nem
 * conversationDeleteModalText. O pai agora so abre o modal e reage a `deleted`.
 *
 * O <Teleport to="body"> e obrigatorio: a coluna da lista tem overflow, e sem
 * o teleport o backdrop nasce dentro dela e some.
 */
import { ref, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  open: { type: Boolean, default: false },
  conversation: { type: Object, default: null },
});
const emit = defineEmits(['close', 'deleted']);

const store = useStore();
const { t } = useI18n();

const TEXT = {
  cancel: 'Cancelar',
  closeAriaLabel: 'Cancelar exclusao',
  confirm: 'Apagar',
  confirming: 'Excluindo...',
};

const isDeleting = ref(false);
const canDelete = computed(() => !isDeleting.value);

const title = computed(() => {
  const contact = props.conversation?.contact || props.conversation?.meta?.sender || {};
  const contactLabel =
    contact.phone_number || contact.phoneNumber || contact.name || '';

  return contactLabel
    ? `Deseja apagar a conversa com ${contactLabel}?`
    : 'Deseja apagar a conversa?';
});

const description = computed(
  () => 'As mensagens serao removidas de todos os dispositivos.'
);

function close() {
  if (isDeleting.value) return;
  emit('close');
}

async function confirmDelete() {
  if (!canDelete.value || !props.conversation?.id) return;

  isDeleting.value = true;
  try {
    await store.dispatch('deleteConversation', props.conversation.id);
    isDeleting.value = false;
    emit('deleted');
    useAlert(t('CONVERSATION.SUCCESS_DELETE_CONVERSATION'));
  } catch (error) {
    isDeleting.value = false;
    useAlert(t('CONVERSATION.FAIL_DELETE_CONVERSATION'));
  }
}
</script>

<template>
<Teleport to="body">
  <Transition name="team-delete-modal">
    <div
      v-if="open"
      class="teams-page__delete-modal-backdrop fixed inset-0 z-[9999] flex items-center justify-center bg-[rgba(15,23,42,0.55)] p-4"
      tabindex="-1"
      @click.self="close"
      @keydown.esc.stop.prevent="close"
    >
      <div
        class="conversation-delete-modal relative max-h-full overflow-auto bg-n-alpha-3 shadow-md modal-container rtl:text-right skip-context-menu rounded-xl w-[37.5rem]"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="conversation-delete-modal-title"
        aria-describedby="conversation-delete-modal-description"
      >
        <div class="conversation-delete-modal__content">
          <h2
            id="conversation-delete-modal-title"
            class="conversation-delete-modal__title"
          >
            {{ title }}
          </h2>
          <p
            id="conversation-delete-modal-description"
            class="conversation-delete-modal__description"
          >
            {{ description }}
          </p>

          <div class="conversation-delete-modal__actions">
            <button
              type="button"
              class="conversation-delete-modal__cancel"
              :disabled="isDeleting"
              @click="close"
            >
              {{ TEXT.cancel }}
            </button>
            <button
              type="button"
              class="conversation-delete-modal__confirm"
              :disabled="!canDelete"
              @click="confirmDelete"
            >
              {{
                isDeleting
                  ? TEXT.confirming
                  : TEXT.confirm
              }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</Teleport>
</template>

<style scoped>
.conversation-delete-modal {
  z-index: 10000;
  width: min(52rem, calc(100vw - 2rem));
  max-height: calc(100vh - 2rem);
  border: 0;
  border-radius: 2rem;
  background: #ffffff;
  padding: 3rem 3rem 2.25rem;
}

.conversation-delete-modal__content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.conversation-delete-modal__title {
  margin: 0;
  color: #111827;
  font-size: 1.5rem;
  font-weight: 500;
  line-height: 1.25;
}

.conversation-delete-modal__description {
  max-width: 30rem;
  margin: 1.5rem 0 0;
  color: #6b7280;
  font-size: 1.125rem;
  line-height: 1.5;
}

.conversation-delete-modal__actions {
  display: flex;
  width: 100%;
  justify-content: flex-end;
  gap: 1.5rem;
  padding-top: 3rem;
}

.conversation-delete-modal__cancel,
.conversation-delete-modal__confirm {
  display: inline-flex;
  min-width: 9.5rem;
  min-height: 3.5rem;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  padding: 0.75rem 1.75rem;
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.25;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease,
    opacity 0.15s ease;
}

.conversation-delete-modal__cancel {
  border: 0;
  background: transparent;
  color: #278c68;
}

.conversation-delete-modal__cancel:hover:not(:disabled) {
  background: #f0fdf4;
  color: #19704f;
}

.conversation-delete-modal__confirm {
  border: 0;
  background: #f0003c;
  color: #ffffff;
}

.conversation-delete-modal__confirm:hover:not(:disabled) {
  background: #cf0034;
}

.conversation-delete-modal__cancel:disabled,
.conversation-delete-modal__confirm:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.team-delete-modal-enter-active,
.team-delete-modal-leave-active {
  transition: opacity 0.2s ease;
}

.team-delete-modal-enter-from,
.team-delete-modal-leave-to {
  opacity: 0;
}

:global(.dark) .conversation-delete-modal {
  background: #111315;
}

:global(.dark) .conversation-delete-modal__cancel,
:global(.dark) .conversation-delete-modal__title,
:global(.dark) .conversation-delete-modal__cancel {
  color: #f9fafb;
}

:global(.dark) .conversation-delete-modal__description {
  color: #a1a1aa;
}

:global(.dark) .conversation-delete-modal__cancel:hover:not(:disabled) {
  background: #252b31;
}

@media (max-width: 640px) {
  .conversation-delete-modal {
    width: min(52rem, calc(100vw - 1rem));
    padding: 2rem 1.25rem 1.5rem;
  }

  .conversation-delete-modal__actions {
    flex-direction: column-reverse;
  }

  .conversation-delete-modal__cancel,
  .conversation-delete-modal__confirm {
    width: 100%;
  }
}
</style>
